import React from "react";
import styled from "styled-components";
import { Container, Section, Eyebrow } from "../components/ui/Container";
import { PageHeader } from "../components/ui/PageHeader";
import { ExternalButton } from "../components/ui/Button";
import { TrustScoring } from "../sections/TrustScoring";
import { CTA } from "../sections/CTA";
import { FAQ } from "../sections/FAQ";
import { colors, fonts, media, radius } from "../theme";

const tiers = [
  { tier: "A", returns: "8 to 9%", risk: "Very low", color: colors.success },
  { tier: "B", returns: "9 to 10%", risk: "Low", color: colors.brand },
  { tier: "C", returns: "10 to 11%", risk: "Moderate", color: colors.gold },
  { tier: "D", returns: "11 to 12%", risk: "Higher", color: colors.danger },
];

const benefits = [
  { t: "Invest from UGX 10,000", b: "Each loan is divided into UGX 10,000 units, so you can build a diversified portfolio even with a small starting balance." },
  { t: "Typical 8 to 12% per cycle", b: "Most loans run on 30 to 90 day cycles. You can reinvest your principal and interest as soon as a loan repays." },
  { t: "Principal protection", b: "A first loss reserve covers principal in the rare event of a default, so your capital is protected." },
  { t: "Real time tracking", b: "Watch funded loans, repayments and earnings update in your dashboard as they happen." },
  { t: "Transparent risk ratings", b: "Every loan shows its risk tier, borrower verification and underlying business profile before you commit." },
  { t: "Top up via mobile money", b: "Fund your wallet from MTN, Airtel or your bank, invest immediately, and withdraw any time." },
];

export const Invest = () => (
  <>
    <PageHeader
      eyebrow="For investors"
      title="Earn real returns. Fund real businesses."
      sub="Everpesa Invest connects you to short term, vetted loans to small businesses in Uganda. Typical returns of 8 to 12% per cycle, principal protection, and full transparency on every loan."
    >
      <Row>
        <ExternalButton variant="white" href="https://invest.everpesa.com" target="_blank" rel="noopener noreferrer">
          Open my account
        </ExternalButton>
        <ExternalButton variant="outlineWhite" href="https://invest.everpesa.com" target="_blank" rel="noopener noreferrer">
          Sign in
        </ExternalButton>
      </Row>
    </PageHeader>

    <Section>
      <Container>
        <Eyebrow>Why investors choose Everpesa</Eyebrow>
        <h2 style={{ marginTop: 18, marginBottom: 40, fontFamily: fonts.display }}>
          Returns you can see, in businesses you can visit.
        </h2>
        <Grid>
          {benefits.map((b) => (
            <BCard key={b.t}>
              <h3>{b.t}</h3>
              <p>{b.b}</p>
            </BCard>
          ))}
        </Grid>
      </Container>
    </Section>

    <Section style={{ background: colors.surfaceAlt }}>
      <Container>
        <Eyebrow>Risk tiers</Eyebrow>
        <h2 style={{ marginTop: 18, marginBottom: 16, fontFamily: fonts.display }}>
          Risk priced. Transparent. Investor controlled.
        </h2>
        <p style={{ maxWidth: 640, marginBottom: 40, fontSize: 17 }}>
          Everpesa loans are graded into four tiers based on the borrower's
          verified profile and history. You decide how much risk you want to
          take. Higher tier loans pay more, and we always tell you why.
        </p>

        <Tiers>
          {tiers.map((t) => (
            <Tier key={t.tier} $color={t.color}>
              <TierBadge>Tier {t.tier}</TierBadge>
              <TierReturn>{t.returns}<small>per cycle</small></TierReturn>
              <TierRisk>{t.risk} risk</TierRisk>
            </Tier>
          ))}
        </Tiers>
      </Container>
    </Section>

    <TrustScoring />
    <FAQ />
    <CTA />
  </>
);

const Row = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  ${media.lg} { grid-template-columns: repeat(2, 1fr); }
  ${media.sm} { grid-template-columns: 1fr; }
`;

const BCard = styled.div`
  background: white;
  border: 1px solid ${colors.line};
  border-radius: ${radius.lg};
  padding: 28px;
  transition: transform 220ms ease, border-color 220ms ease;
  &:hover { transform: translateY(-3px); border-color: ${colors.brand}; }
  h3 { font-family: ${fonts.display}; font-size: 19px; margin-bottom: 10px; }
  p { font-size: 15px; line-height: 1.65; }
`;

const Tiers = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  ${media.md} { grid-template-columns: repeat(2, 1fr); }
`;

const Tier = styled.div`
  background: white;
  border: 1px solid ${colors.line};
  border-top: 4px solid ${(p) => p.$color};
  border-radius: ${radius.lg};
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TierBadge = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.inkSoft};
`;

const TierReturn = styled.span`
  font-family: ${fonts.display};
  font-size: 38px;
  font-weight: 800;
  color: ${colors.ink};
  small {
    display: block;
    font-size: 12.5px;
    font-weight: 500;
    color: ${colors.inkSoft};
    margin-top: 2px;
  }
`;

const TierRisk = styled.span`
  font-size: 14px;
  color: ${colors.inkMuted};
`;
